import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { WorldSection } from './WorldPageHeader';
import { CopyTextButton } from './CopyTextButton';
import { buildGuidePendings, categoryLabel, formatGuidePendings, kindLabel, reasonLabel, type PendingExtras } from '../../utils/guidePendings';
import { fetchCsv, parseJson } from '../../utils/csv';
import { controlFile, packageFiles } from '../../data/world/package';
import { loadEventPositions } from '../../utils/eventPositions';
import { loadMapImages } from '../../utils/mapImages';
import type { WorldData } from '../../types/world';

/** Preguntas visibles antes de pulsar «Ver todas». */
const PREVIEW = 20;
const urlOf = (name: string) => packageFiles.find(f => f.filename === name)?.url;

/** Lee los archivos que el mundo cargado no incluye (31, 32 y la fila dataset de 00_control). */
async function loadExtras(): Promise<PendingExtras> {
  const errors: string[] = [];
  const get = async (name: string, url?: string) => {
    if (!url) {
      errors.push(`${name}: sin URL en el paquete`);
      return undefined;
    }
    try {
      const r = await fetchCsv(url);
      r.errors.slice(0, 3).forEach(e => errors.push(`${name}: ${e}`));
      return r.rows;
    } catch (e) {
      errors.push(`${name}: ${e instanceof Error ? e.message : 'error de descarga'}`);
      return undefined;
    }
  };
  const [ep, control, mapImages] = await Promise.all([loadEventPositions(), get('00_control.csv', controlFile.url), loadMapImages().catch((e: unknown) => {
    errors.push(`47_map_images.csv: ${e instanceof Error ? e.message : 'error de descarga'}`);
    return undefined;
  })]);
  errors.push(...ep.errors);
  const eventMonsters = ep.eventMonsters.length || !ep.errors.some(e => e.startsWith('31_')) ? ep.eventMonsters : undefined;
  const eventSpawnRules = ep.spawnRules.length || !ep.errors.some(e => e.startsWith('32_')) ? ep.spawnRules : undefined;
  let controlPending: unknown;
  if (control) {
    const ds = control.find(r => r.record_type === 'dataset');
    const details = ds?.details_json ? parseJson<Record<string, unknown>>(ds.details_json) : undefined;
    if (!ds) errors.push('00_control.csv: sin fila record_type = dataset');else if (!details) errors.push('00_control.csv: details_json de la fila dataset no es JSON válido');else if (details.pending_requirements === undefined) errors.push('00_control.csv: details_json no contiene pending_requirements');else controlPending = details.pending_requirements;
  }
  return {
    eventMonsters,
    eventSpawnRules,
    positions: ep.positions,
    controlPending,
    mapImages,
    errors
  };
}

/** Lista consolidada de pendientes de toda la guía, con un único botón para copiarla. */
export function GuidePendings({
  w
}: {
  w: WorldData;
}) {
  const [extras, setExtras] = useState<PendingExtras | undefined>();
  useEffect(() => {
    let alive = true;
    loadExtras().then(x => alive && setExtras(x));
    return () => {
      alive = false;
    };
  }, []);
  const r = useMemo(() => extras ? buildGuidePendings(w, extras) : undefined, [w, extras]);
  const text = useMemo(() => r ? formatGuidePendings(r) : '', [r]);
  const [showAll, setShowAll] = useState(false);
  if (!r) {
    return <WorldSection id="pendientes" title="Pendientes de la guía" data-id="element-1734" data-mp="3v6dgy48qi64i">
        <p className="text-sm text-gray-400" role="status" data-id="element-1735" data-mp="3r5jsn9rdqpb3">Leyendo 31_event_monsters, 32_event_spawn_rules y 00_control.csv…</p>
      </WorldSection>;
  }
  const admin = r.pendings.filter(p => p.needsAdmin);
  const internal = r.pendings.length - admin.length;
  const fmt = (p: (typeof admin)[number], i: number) => `${i + 1}. [${categoryLabel[p.category]}] ${p.name}\n   Pregunta: ${p.question}\n   Falta: ${p.missing}\n   Se sabe: ${p.known}\n   Estado: ${reasonLabel[p.reason]} · Evidencia: ${p.evidence.join(', ')}\n   Clave: ${p.key}${p.refs?.length ? ` (también ${p.refs.join(', ')})` : ''}`;
  // Los datos que resuelve una solicitud de archivos van dentro de esa solicitud (un estado por dato), no como preguntas sueltas.
  const files = admin.filter(p => p.key.startsWith('source:'));
  const sourced = admin.filter(p => p.source);
  const decisions = admin.filter(p => p.kind !== 'archivo');
  const top = [...files, ...decisions];
  const dataOf = (key: string) => sourced.filter(p => p.source === key);
  const questionsText = [`PENDIENTES PARA ADMINISTRACIÓN — MU VANGUARD (${top.length})`, '', `## ${kindLabel.archivo} (${files.length} solicitudes · ${sourced.length} datos)`, ...files.map((p, i) => [fmt(p, i), ...dataOf(p.key).map(d => `   · ${d.name} [${d.key}] — Falta: ${d.missing} — Estado: ${reasonLabel[d.reason]}`)].join('\n')), '', `## ${kindLabel.administracion} (${decisions.length})`, ...decisions.map(fmt)].join('\n');
  const counts = Object.entries(categoryLabel).map(([k, label]) => ({
    label,
    n: top.filter(p => p.category === k).length
  })).filter(c => c.n);
  return <WorldSection id="pendientes" title="Pendientes de la guía" data-id="element-1736" data-mp="2j768brmz16qk">
      <p className="text-sm text-gray-400" data-id="element-1737" data-mp="2j7ellkn0ae2a">
        Calculado en este navegador con la misma lógica que las fichas. Cada fila es una sola pregunta, aunque el dato aparezca en varias fichas. Responde con la clave y se integrará como «Confirmado por administración» con fecha.
      </p>

      {r.extrasErrors.length > 0 && <ul className="mt-3 rounded-xl border border-red-400/30 p-3 text-sm text-red-200" data-id="element-1738" data-mp="2n6x82kuh6x78">
          {r.extrasErrors.map(e => <li key={e} data-id="element-1739" data-mp="2wytd6fuzbnky">{e}</li>)}
        </ul>}

      <div className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-2" data-id="element-1740" data-mp="2wybc95xdgl6p">
        <p className="font-cinzel text-2xl font-bold text-white" data-id="element-1741" data-mp="3axawm1duqdx2">{top.length} pendientes para administración</p>
        <p className="text-sm text-gray-300" data-id="element-1742" data-mp="36x8x9yvl9wky">{files.length} solicitudes de archivos (resuelven {sourced.length} datos) · {decisions.length} decisiones · {r.pageChecks.length + r.tests.length} pruebas</p>
        <p className="text-sm text-gray-400" data-id="element-1743" data-mp="1ckapblxd3c0s">
          {internal} problemas internos · {r.resolvedByAdmin.length} respondidos por administración · {r.resolvedByAudit.length} resueltos con la auditoría técnica · catálogo {r.catalog.published} publicados de {r.catalog.raw} ({r.catalog.excluded.length} excluidos)
        </p>
      </div>
      <ul className="mt-3 flex flex-wrap gap-2 text-sm" data-id="element-1744" data-mp="3qkqom1b6k67s">
        {counts.map(c => <li key={c.label} className="rounded-full border border-white/10 px-3 py-1 text-gray-300" data-id="element-1745" data-mp="3qidz9333etkp">
            {c.label} <span className="font-mono font-semibold text-white" data-id="element-1746" data-mp="3g12rx66fvtt">{c.n}</span>
          </li>)}
      </ul>

      <div className="mt-4 flex flex-wrap gap-2" data-id="element-1747" data-mp="870w6sg37vrk">
        <CopyTextButton text={questionsText} label={`Copiar los ${top.length} pendientes`} data-id="element-1748" data-mp="87j4ing0y0fu" />
        <CopyTextButton text={text} label="Copiar informe completo" data-id="element-1749" data-mp="d5awtikkzokk" />
      </div>

      <h3 className="mt-6 text-sm font-semibold text-white" data-id="element-1750" data-mp="i5ynu48t85es">
        {showAll ? `Los ${top.length} pendientes` : `Primeros ${Math.min(PREVIEW, top.length)} de ${top.length} pendientes`}
      </h3>
      <ol className="mt-2 divide-y divide-white/5 rounded-2xl border border-white/10" data-id="element-1751" data-mp="26pm7rsijj8gj">
        {(showAll ? top : top.slice(0, PREVIEW)).map((p, i) => <li key={p.key} className="grid gap-1 px-4 py-3 text-sm md:grid-cols-[2rem_1fr]" data-id="element-1752" data-mp="1xnv6czv19nsh" data-mpi={i}>
            <span className="font-mono text-gray-500" data-id="element-1753" data-mp="1xpppq9qnp7ug">{i + 1}</span>
            <div className="min-w-0" data-id="element-1754" data-mp="1sqcvv51db0id">
              <p className="font-semibold text-white" data-id="element-1755" data-mp="2qck8913wxx5a">{p.name}</p>
              <p className="text-gray-300" data-id="element-1756" data-mp="2qbqyrsdkktjg">{p.question}</p>
              <p className="mt-1 text-xs text-gray-500" data-id="element-1757" data-mp="2ld7b9njbv9oz">
                {categoryLabel[p.category]} · {reasonLabel[p.reason]} · Evidencia: {p.evidence.join(', ')}
                {p.affects ? ` · afecta a ${p.affects}` : ''}
              </p>
              <p className="mt-1 flex flex-wrap gap-x-3 text-xs" data-id="element-1758" data-mp="2bne2svehfaoy">
                {p.links.filter(x => x.startsWith('/')).slice(0, 3).map(to => <Link key={to} to={to} className="text-[#D4AF37] underline underline-offset-4" data-id="element-1759" data-mp="34c1r6zuj2g6q">{to}</Link>)}
                <span className="font-mono text-gray-600" data-id="element-1760" data-mp="2cnyt8cpp8f0b">{p.key}</span>
              </p>
              {dataOf(p.key).length > 0 && <details className="mt-2 text-xs" data-id="element-1761" data-mp="2hhbfwhatibqf">
                  <summary className="cursor-pointer font-semibold text-gray-300" data-id="element-1762" data-mp="23p3n10mnn4x0">{dataOf(p.key).length} datos que resuelve, cada uno con su estado</summary>
                  <ul className="mt-1.5 space-y-1 text-gray-400" data-id="element-1763" data-mp="2vf8aj85po82g">
                    {dataOf(p.key).map(d => <li key={d.key} data-id="element-1764" data-mp="30euojjtd5scb">
                        <span className="text-gray-200" data-id="element-1765" data-mp="30dt2yleq1ngc">{d.name}</span> · {reasonLabel[d.reason]} · <span className="font-mono text-gray-600" data-id="element-1766" data-mp="2mdia5ctupysj">{d.key}</span>
                      </li>)}
                  </ul>
                </details>}
            </div>
          </li>)}
      </ol>
      {top.length > PREVIEW && <button type="button" onClick={() => setShowAll(v => !v)} aria-expanded={showAll} className="mt-3 h-9 rounded-xl border border-white/10 px-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1767" data-mp="1fy7t9xyn1zn4">
          {showAll ? `Mostrar solo los primeros ${PREVIEW}` : `Ver los ${top.length} pendientes`}
        </button>}

      <h3 className="mt-6 text-sm font-semibold text-white" data-id="element-1768" data-mp="1cgahawf8zqgt">Secciones revisadas</h3>
      <ul className="mt-2 space-y-1 text-sm" data-id="element-1769" data-mp="oqpsvy0bs105">
        {r.coverage.map(c => <li key={c.section} className={c.reviewed ? 'text-gray-300' : 'text-amber-200'} data-id="element-1770" data-mp="jrwhcb4lmk2m">
            {c.reviewed ? 'Revisada' : 'No revisada'} · {c.section} <span className="text-gray-500" data-id="element-1771" data-mp="yhkj1dgozkqv">— {c.note}</span>
          </li>)}
      </ul>
    </WorldSection>;
}